import React, { useEffect, useState } from "react";
import { theme } from "../../constants/StyledComponents";
import { IconManager } from "../core/IconManager/IconManager";
import { ReactComponent as HourglassIcon } from "../../icons/hourglass.svg";
import { IconSizeEnum } from "../../types/AppTypes";

type CircleProgressBarProps = {
  valuePercentage: number;
  size: number;
  strokeWidth: number;
  strokeColor: string;
  bgStrokeColor: string;
};

export const CircleProgressBar: React.FC<CircleProgressBarProps> = ({
  valuePercentage,
  size,
  strokeWidth,
  strokeColor,
  bgStrokeColor,
}) => {
  const padding = 5;
  const radius = (size - strokeWidth) / 2;
  const circumference = Math.PI * radius;
  const offset = circumference - (valuePercentage / 100) * circumference;

  const [percentage, setPercentage] = useState(0);

  useEffect(() => {
    if (percentage < valuePercentage) {
      const interval = setInterval(() => {
        setPercentage((prev) =>
          prev < valuePercentage ? prev + 1 : valuePercentage,
        );
      }, 1);
      return () => clearInterval(interval);
    }
  }, [percentage, valuePercentage]);

  const endAngle = (percentage / 100) * 180 - 180;
  const centerX = (size + padding * 2) / 2;
  const centerY = (size + padding * 2) / 2;
  const endX = centerX + radius * Math.cos((endAngle * Math.PI) / 180);
  const endY = centerY + radius * Math.sin((endAngle * Math.PI) / 180);

  const textBottomMargin = 20;
  const effectiveHeight = centerY + textBottomMargin;

  const viewBoxWidth = size + padding * 2;
  const viewBoxHeight = effectiveHeight;

  const percentageY = centerY - 5;
  const legendY = centerY + 20;

  return (
    <svg
      width={size + padding * 2}
      height={viewBoxHeight}
      viewBox={`0 0 ${viewBoxWidth} ${viewBoxHeight}`}
    >
      <path
        d={`
        M ${centerX},${centerY}
        m -${radius},0
        a ${radius},${radius} 0 1,1 ${radius * 2},0
      `}
        fill="none"
        stroke={bgStrokeColor}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
      />
      <path
        d={`
        M ${centerX},${centerY}
        m -${radius},0
        a ${radius},${radius} 0 1,1 ${radius * 2},0
      `}
        fill="none"
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        strokeDasharray={circumference}
        strokeDashoffset={circumference}
        strokeLinecap="round"
      >
        <animate
          attributeName="stroke-dashoffset"
          from={circumference}
          to={offset}
          dur=".2s"
          fill="freeze"
        />
      </path>
      <circle
        cx={endX}
        cy={endY}
        r={strokeWidth / 1.1}
        fill={theme.colors.green}
      />
      <circle cx={endX} cy={endY} r={strokeWidth / 1.7} fill={"white"} />
      <foreignObject
        x={centerX - 8}
        y={centerY - radius * 0.6}
        width="16px"
        height="16px"
      >
        <IconManager color={theme.colors.purple_2} size={IconSizeEnum.XSMALL}>
          <HourglassIcon />
        </IconManager>
      </foreignObject>
      <text
        x="50%"
        y={percentageY}
        textAnchor="middle"
        style={{
          fontSize: "18px",
          fontWeight: "600",
          fill: `${theme.colors.black}`,
        }}
      >
        {percentage}%
      </text>
      <text
        x="10%"
        y={legendY}
        textAnchor="middle"
        style={{
          fontSize: "12px",
          fontWeight: "500",
          fill: `${theme.colors.black}`,
        }}
      >
        0%
      </text>
      <text
        x="90%"
        y={legendY}
        textAnchor="middle"
        style={{
          fontSize: "12px",
          fontWeight: "500",
          fill: `${theme.colors.black}`,
          lineHeight: 0,
        }}
      >
        100%
      </text>
    </svg>
  );
};
